import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart2 } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { RiskScoreCircle } from "@/components/common/RiskScoreCircle";
import { StatusBadge } from "@/components/common/StatusBadge";
import { RiskHeatMap, type HeatMapCell } from "@/components/common/RiskHeatMap";
import { toast } from "@/components/ui/Toast";
import type { RiskLevel, AssessmentStatus } from "@/types";
import { toRiskScore } from "@/lib/utils";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// AssessmentTab
//
// Tab showing risk score, per-posture bars, and heat map.
// Only shown when assessment is sufficiently complete.
// Design: §6.3 Tab 5 — Assessment
// ─────────────────────────────────────────────

export interface PostureScore {
  postureId: string;
  postureName: string;
  postureCode: string;
  score: number;
  riskLevel: RiskLevel;
  likelihoodScore: number;
  impactScore: number;
}

interface AssessmentTabProps {
  assessmentId: string;
  overallScore: number | null;
  riskLevel: RiskLevel | null;
  postureScores: PostureScore[];
  completionPct: number;
  completionThreshold?: number;
  // Metadata fields for the Overall Risk Score panel
  version?: number;
  status?: AssessmentStatus;
  createdAt?: string;
  updatedAt?: string;
  schemaName?: string;
  entityName?: string;
  assignedTo?: string | null;
}

const RISK_COLORS: Record<RiskLevel, string> = {
  LOW: "var(--success)",
  MEDIUM: "var(--warning)",
  HIGH: "var(--k-orange)",
  VERY_HIGH: "var(--k-purple)",
  CRITICAL: "var(--danger)",
};

export function AssessmentTab({
  assessmentId,
  overallScore,
  riskLevel,
  postureScores,
  completionPct,
  completionThreshold = 50,
  version,
  status,
  createdAt,
  updatedAt,
  schemaName,
  entityName,
  assignedTo,
}: AssessmentTabProps) {
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  const isSufficient = completionPct >= completionThreshold;

  const RISK_LABELS: Record<RiskLevel, string> = {
    LOW: t("risk.low"),
    MEDIUM: t("risk.medium"),
    HIGH: t("risk.high"),
    VERY_HIGH: t("risk.veryHigh"),
    CRITICAL: t("risk.critical"),
  };

  const scoreMutation = useMutation({
    mutationFn: () => api.post(`/assessments/${assessmentId}/score`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] });
      toast.success(t("assessment.scoreCalculated"));
    },
    onError: (err: Error) => toast.error(err.message ?? t("assessment.scoreCalcFailed")),
  });

  // Not enough responses yet — block on completion, not on score existence
  if (!isSufficient) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: "var(--bg-hover)" }}
        >
          <BarChart2 className="h-8 w-8 text-text-muted" />
        </div>
        <div className="text-center">
          <p className="text-[16px] font-semibold text-text-primary">
            {t("assessment.notAvailable")}
          </p>
          <p className="mt-2 text-[14px] text-text-muted max-w-md">
            {`${t("assessment.completeAtLeast")} ${completionThreshold}${t("assessment.ofQuestionnaireToView")}`}{" "}
            {`${t("assessment.currentlyAtPct")} ${Math.round(completionPct)}%.`}
          </p>
        </div>
        {/* Mini progress bar */}
        <div className="flex items-center gap-3">
          <div className="h-2 w-48 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${completionPct}%`,
                backgroundColor: "var(--accent)",
              }}
            />
          </div>
          <span className="text-[12px] text-text-muted">{Math.round(completionPct)}%</span>
        </div>
      </div>
    );
  }

  // WP04/L-045: the score is server-driven — it is computed and persisted when
  // the assessment is approved, and on demand via the button below. We no longer
  // auto-POST /score on mount: that fired twice per view (StrictMode/re-render),
  // permanently accumulating duplicate score rows, and would spam read-only
  // Viewers with 403s now that scoring requires the 'update' ability (S-018).

  // Sufficient completion but no score calculated yet — show Calculate button as fallback
  if (overallScore === null || riskLevel === null) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: "var(--bg-hover)" }}
        >
          <BarChart2 className="h-8 w-8 text-text-muted" />
        </div>
        <div className="text-center">
          <p className="text-[16px] font-semibold text-text-primary">{t("assessment.scoreNotCalculated")}</p>
          <p className="mt-2 text-[14px] text-text-muted max-w-md">
            {`${t("assessment.questionnaireIsPct")} ${Math.round(completionPct)}${t("assessment.completeCalculateScore")}`}
          </p>
        </div>
        <Button
          onClick={() => scoreMutation.mutate()}
          isLoading={scoreMutation.isPending}
          leadingIcon={<BarChart2 className="h-4 w-4" />}
        >
          {t("assessment.calculateScore")}
        </Button>
      </div>
    );
  }

  // Build heat map cells from posture scores
  // Group postures by their matrix cell
  const cellGroupMap = new Map<string, string[]>();
  for (const ps of postureScores) {
    const l = Math.max(1, Math.min(5, Math.round(ps.likelihoodScore)));
    const i = Math.max(1, Math.min(5, Math.round(ps.impactScore)));
    const cellId = `L${l}_I${i}`;
    const existing = cellGroupMap.get(cellId) ?? [];
    cellGroupMap.set(cellId, [...existing, ps.postureCode]);
  }
  const heatMapCells: HeatMapCell[] = Array.from(cellGroupMap.entries()).map(
    ([cellId, labels]) => ({
      cellId: cellId as HeatMapCell["cellId"],
      count: labels.length,
      labels,
    })
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Top row: score + heat map */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Risk score circle + metadata */}
        <div className="rounded-card border border-border-default bg-card p-6 flex flex-col gap-4">
          <h3 className="text-[14px] font-semibold text-text-primary">{t("reports.overallRiskScore")}</h3>
          <div className="flex justify-center">
            <RiskScoreCircle score={overallScore} riskLevel={riskLevel} />
          </div>
          {/* Metadata fields */}
          <div className="flex flex-col gap-2 border-t border-border-default pt-4 mt-1">
            {status && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("entity.status")}</span>
                <StatusBadge type="assessment" status={status} />
              </div>
            )}
            {version !== undefined && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("assessment.version")}</span>
                <span className="text-[13px] font-medium text-text-secondary">v{version}</span>
              </div>
            )}
            {entityName && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("assessment.entity")}</span>
                <span className="text-[13px] font-medium text-text-secondary truncate max-w-[140px] text-right">{entityName}</span>
              </div>
            )}
            {schemaName && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("assessment.schema")}</span>
                <span className="text-[13px] font-medium text-text-secondary truncate max-w-[140px] text-right">{schemaName}</span>
              </div>
            )}
            {createdAt && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("common.created")}</span>
                <span className="text-[13px] text-text-secondary">
                  {formatDate(createdAt, language)}
                </span>
              </div>
            )}
            {updatedAt && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("assessment.lastUpdated")}</span>
                <span className="text-[13px] text-text-secondary">
                  {formatDate(updatedAt, language)}
                </span>
              </div>
            )}
            {assignedTo && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-text-muted">{t("assessment.assignedTo")}</span>
                <span className="text-[13px] font-medium text-text-secondary truncate max-w-[140px] text-right">{assignedTo}</span>
              </div>
            )}
          </div>
        </div>

        {/* Heat map */}
        <div className="rounded-card border border-border-default bg-card p-5 lg:col-span-2">
          <h3 className="mb-4 text-[14px] font-semibold text-text-primary">{t("risk.heatMap")}</h3>
          <RiskHeatMap cells={heatMapCells} />
        </div>
      </div>

      {/* Per-posture bars */}
      {postureScores.length > 0 && (
        <div className="rounded-card border border-border-default bg-card p-5">
          <h3 className="mb-4 text-[14px] font-semibold text-text-primary">{t("reports.postureBreakdown")}</h3>
          <div className="flex flex-col gap-3">
            {postureScores.map((ps) => (
              <div key={ps.postureId} className="flex items-center gap-3">
                <span className="w-48 flex-shrink-0 truncate text-[13px] text-text-secondary">
                  {ps.postureName}
                </span>
                <div className="flex-1 h-2 overflow-hidden rounded-full" style={{ backgroundColor: "var(--bg-hover)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${toRiskScore(ps.score)}%`,
                      backgroundColor: RISK_COLORS[ps.riskLevel],
                    }}
                  />
                </div>
                <span className="w-12 text-right text-[12px] font-medium text-text-muted">
                  {toRiskScore(ps.score)}%
                </span>
                <span
                  className="w-16 rounded-badge px-2 py-0.5 text-center text-[11px] font-semibold"
                  style={{
                    backgroundColor: `${RISK_COLORS[ps.riskLevel]}22`,
                    color: RISK_COLORS[ps.riskLevel],
                  }}
                >
                  {RISK_LABELS[ps.riskLevel]}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
